/**
 * QuotesTableDesktop Component
 * Desktop-only table with simplified columns and side panel for details
 */

import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Drawer,
  Typography,
  IconButton,
  Button,
  Divider,
  Chip,
  Checkbox,
} from "@mui/material";
import {
  Close,
  Send,
  Edit,
  CheckCircle,
  Message,
  Visibility,
} from "@mui/icons-material";
import { formatCurrency, formatDateShort } from "../../../shared/utils/formatters";
import { getPaymentStatus } from "../utils/paymentStatus";
import { formatQuoteMoveDate } from "../utils/moveDateUtils";
import FollowUpMention from "../../followups/components/FollowUpMention";
import {
  getQuotePdfStatus,
  getQuoteItemsListPdfStatus,
  getQuotePdfStatusLabel,
  getCombinedPdfStatusLabel,
  getCombinedPdfStatusColor,
  isQuotePdfPreparing,
} from "../utils/quotePdfReadiness";
import QuoteOperationalStatusBadge from "./QuoteOperationalStatusBadge.jsx";
import QuoteNextActionChip from "./QuoteNextActionChip.jsx";
import {
  formatQuoteAge,
  formatQuoteMoney,
  getQuoteDeposit,
  getQuoteTotal,
} from "../services/quoteOperationalStatus.js";

/**
 * Format route string from quote
 */
const formatRoute = (quote) => {
  if (!quote.from || !quote.to) return "Route not specified";
  return `${quote.from} → ${quote.to}`;
};

/**
 * QuotesTableDesktop Component
 */
export const QuotesTableDesktop = ({
  quotes = [],
  permissions = [],
  onRowClick,
  onViewQuote,
  onEditQuote,
  onSendQuote,
  onResendQuote,
  onCreateOrder,
  onWhatsApp,
  onRouteClick,
  selectedQuote,
  onCloseDetailPanel,
  selectionMode = false,
  selectedQuoteIds = new Set(),
  onToggleQuoteSelection,
  onToggleSelectAll,
  showPdfStatus = false,
  onGeneratePdf,
  generatingPdfIds = new Set(),
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);

  const columns = useMemo(() => {
    const base = [
      { key: "customer", label: "Customer", width: 180 },
      { key: "route", label: "Route", width: 200 },
      { key: "moveDate", label: "Move date", width: 130 },
      { key: "quote", label: "Quote", width: 150 },
      { key: "total", label: "Total", width: 110 },
      { key: "deposit", label: "Deposit", width: 110 },
    ];

    if (showPdfStatus) {
      base.push({ key: "pdf", label: "PDF", width: 160 });
    } else {
      base.push({ key: "status", label: "Status", width: 140 });
      base.push({ key: "next", label: "Next action", width: 140 });
      base.push({ key: "age", label: "Age", width: 90 });
    }

    if (selectionMode) {
      base.push({ key: "send", label: "Send", width: 100 });
    }

    return base;
  }, [selectionMode, showPdfStatus]);

  const allSelected =
    quotes.length > 0 && quotes.every((quote) => selectedQuoteIds.has(quote.id));
  const someSelected = quotes.some((quote) => selectedQuoteIds.has(quote.id));

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              {selectionMode && (
                <TableCell
                  padding="checkbox"
                  sx={{ fontWeight: 600, backgroundColor: "grey.50" }}
                >
                  <Checkbox
                    indeterminate={someSelected && !allSelected}
                    checked={allSelected}
                    onChange={(event) => {
                      event.stopPropagation();
                      onToggleSelectAll?.(event.target.checked);
                    }}
                    inputProps={{ "aria-label": "Select all quotes" }}
                  />
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "grey.50",
                    width: col.width,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {quotes.map((quote) => {
              const totalCost = getQuoteTotal(quote);
              const deposit = getQuoteDeposit(quote);
              const route = formatRoute(quote);
              const pdfStatus = getQuotePdfStatus(quote);
              const combinedPdfLabel = getCombinedPdfStatusLabel(quote);
              const combinedPdfColor = getCombinedPdfStatusColor(quote);
              const isSelected = selectedQuoteIds.has(quote.id);

              return (
                <TableRow
                  key={quote.id}
                  hover
                  selected={isSelected}
                  onClick={() => {
                    if (onRowClick) {
                      onRowClick(quote);
                    } else if (onViewQuote) {
                      onViewQuote(quote);
                    }
                  }}
                  onMouseEnter={() => setHoveredRow(quote.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor:
                      hoveredRow === quote.id ? "action.hover" : "transparent",
                  }}
                >
                  {selectionMode && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => onToggleQuoteSelection?.(quote.id)}
                        inputProps={{
                          "aria-label": `Select quote ${quote.id}`,
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {quote.customerName || "Unknown"}
                        {quote?.hasPendingCallRequest ? " 🙋" : ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {quote.customerPhone || "No phone"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {quote.from && quote.to ? (
                      <Typography
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onRouteClick) {
                            onRouteClick(quote, e);
                          }
                        }}
                        sx={{
                          cursor: "pointer",
                          color: "primary.main",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {route}
                      </Typography>
                    ) : (
                      <Typography variant="body2">{route}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatQuoteMoveDate(quote)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                    >
                      {quote.referenceNumber || quote.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "success.dark" }}
                    >
                      {totalCost > 0 ? formatQuoteMoney(totalCost) : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "warning.dark" }}
                    >
                      {deposit > 0 ? formatQuoteMoney(deposit) : "—"}
                    </Typography>
                  </TableCell>
                  {showPdfStatus ? (
                    <TableCell>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Chip
                          label={combinedPdfLabel}
                          color={combinedPdfColor}
                          size="small"
                          sx={{ width: "fit-content", fontWeight: 500 }}
                        />
                        {(pdfStatus !== "ready" ||
                          getQuoteItemsListPdfStatus(quote) !== "ready") && (
                          <Typography variant="caption" color="text.secondary">
                            Summary: {getQuotePdfStatusLabel(pdfStatus)} · Items
                            list:{" "}
                            {getQuotePdfStatusLabel(
                              getQuoteItemsListPdfStatus(quote)
                            )}
                          </Typography>
                        )}
                        {isQuotePdfPreparing(quote) && (
                          <Typography variant="caption" color="text.secondary">
                            PDFs regenerate when you send email
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  ) : (
                    <>
                      <TableCell>
                        <QuoteOperationalStatusBadge quote={quote} />
                      </TableCell>
                      <TableCell>
                        <QuoteNextActionChip quote={quote} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatQuoteAge(quote)}
                        </Typography>
                      </TableCell>
                    </>
                  )}
                  {selectionMode && (
                    <TableCell>
                      {permissions.includes("quotes:send") && onSendQuote && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Send />}
                          onClick={(event) => {
                            event.stopPropagation();
                            onSendQuote(quote);
                          }}
                        >
                          Send
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Side Detail Panel */}
      <Drawer
        anchor="right"
        open={!!selectedQuote}
        onClose={onCloseDetailPanel}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 400, md: 500 } },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Quote Details</Typography>
            <IconButton onClick={onCloseDetailPanel} size="small">
              <Close />
            </IconButton>
          </Box>

          {/* Content */}
          {selectedQuote && (
            <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
              {/* Quote Info */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="caption"
                  sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                >
                  {selectedQuote.id}
                </Typography>
                <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
                  {selectedQuote.customerName || "Unknown"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatRoute(selectedQuote)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Move date: {formatQuoteMoveDate(selectedQuote)}
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Payment Breakdown */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Payment Breakdown
                </Typography>
                <Box
                  sx={{
                    bgcolor: "grey.50",
                    p: 2,
                    borderRadius: 1,
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">Total:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(getQuoteTotal(selectedQuote))}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">Paid:</Typography>
                    <Typography variant="body2">
                      {formatCurrency(selectedQuote?.payments?.totalPaid || 0)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2">Balance:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(selectedQuote?.payments?.balance || 0)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={getPaymentStatus(selectedQuote).label}
                    color={getPaymentStatus(selectedQuote).color}
                    variant={getPaymentStatus(selectedQuote).variant}
                    size="small"
                  />
                </Box>
              </Box>

              {/* Follow-ups */}
              {selectedQuote?.followUpSummary?.count > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Follow-ups
                  </Typography>
                  <FollowUpMention
                    summary={selectedQuote.followUpSummary}
                    onClick={
                      selectedQuote?.onFollowUpNavigate
                        ? () => selectedQuote.onFollowUpNavigate(selectedQuote)
                        : undefined
                    }
                  />
                </Box>
              )}

              {/* Created Date */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Created
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateShort(selectedQuote.createdAt)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Actions Footer */}
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {onWhatsApp && (
              <Button
                variant="contained"
                fullWidth
                startIcon={<Message />}
                onClick={() => {
                  onWhatsApp(selectedQuote);
                }}
                sx={{
                  backgroundColor: "#25d366",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#20ba5a",
                  },
                }}
              >
                WhatsApp
              </Button>
            )}
            {onViewQuote && (
              <Button
                variant="contained"
                fullWidth
                startIcon={<Visibility />}
                onClick={() => {
                  onViewQuote(selectedQuote);
                  onCloseDetailPanel();
                }}
              >
                View Full Details
              </Button>
            )}
            {permissions.includes("quotes:update") && onEditQuote && (
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Edit />}
                onClick={() => {
                  onEditQuote(selectedQuote);
                  onCloseDetailPanel();
                }}
              >
                Edit Quote
              </Button>
            )}
            {selectedQuote?.status === "pending" &&
              permissions.includes("quotes:send") &&
              onSendQuote && (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Send />}
                  onClick={() => {
                    onSendQuote(selectedQuote);
                    onCloseDetailPanel();
                  }}
                >
                  Send Email
                </Button>
              )}
            {(selectedQuote?.status === "sent" ||
              selectedQuote?.status === "negotiating") &&
              permissions.includes("quotes:send") &&
              onResendQuote && (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Send />}
                  onClick={() => {
                    onResendQuote(selectedQuote);
                    onCloseDetailPanel();
                  }}
                >
                  Resend Email
                </Button>
              )}
            {selectedQuote?.status === "accepted" &&
              permissions.includes("orders:create") &&
              onCreateOrder && (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<CheckCircle />}
                  onClick={() => {
                    onCreateOrder(selectedQuote);
                    onCloseDetailPanel();
                  }}
                >
                  Create Order
                </Button>
              )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default QuotesTableDesktop;

